import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/**
 * The words of an address, lowercased, with punctuation dropped and a run of letters split from a
 * run of digits: "1074S" is a house number and a street, however it was typed.
 */
function tokenize(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/(\d)([a-z])/g, '$1 $2').replace(/([a-z])(\d)/g, '$1 $2').trim().split(' ').filter(Boolean);
}

/**
 * The words a property manager types to say which part of a lot the property is — "1074 S Genesee
 * Ave HSE" is the house on a lot that also has apartments. A provider prints the address the meter
 * is at and rarely repeats them, so they are worth setting aside once the addresses have failed to
 * match with them in place.
 */
const UNIT_DESIGNATORS = new Set(['hse', 'house', 'apt', 'apartment', 'unit', 'ste', 'suite', 'bldg', 'building', 'fl', 'floor', 'rm', 'room', 'lot', 'spc', 'space', 'trlr', 'trailer']);

/**
 * The words of an address that have one accepted short form. A provider prints what fits on a line
 * and a property manager types what they say out loud, so "South Genesee Avenue" and "S Genesee Ave"
 * are one street — while a short form no word here has ("Blvd") stays the street it names.
 */
const ABBREVIATIONS = {
  street: 'st',
  avenue: 'ave',
  boulevard: 'blvd',
  drive: 'dr',
  road: 'rd',
  lane: 'ln',
  court: 'ct',
  place: 'pl',
  terrace: 'ter',
  circle: 'cir',
  parkway: 'pkwy',
  highway: 'hwy',
  square: 'sq',
  trail: 'trl',
  north: 'n',
  south: 's',
  east: 'e',
  west: 'w',
  northeast: 'ne',
  northwest: 'nw',
  southeast: 'se',
  southwest: 'sw'
};

// How much of an address the shorter side must be before one containing the other is accepted — a
// fragment matches half a portfolio. And how far in a house number can appear before a number is
// something else, the postcode above all.
const MIN_CONTAINMENT_LENGTH = 10;
const HOUSE_NUMBER_SEARCH_DEPTH = 3;
const isNumber = token => /^\d+$/.test(token);
const isUnitNumber = token => isNumber(token) || token.length === 1;
const houseNumber = tokens => tokens.slice(0, HOUSE_NUMBER_SEARCH_DEPTH).find(isNumber) || null;

/** Which unit the address names, when it names one: the "3" of "Apt 3". */
function unitNumber(tokens) {
  const at = tokens.findIndex((token, index) => UNIT_DESIGNATORS.has(token) && index + 1 < tokens.length && isUnitNumber(tokens[index + 1]));
  return at < 0 ? null : tokens[at + 1];
}

/**
 * The postcode, read as the last five-figure number that is not the house number. Nothing is read
 * from an address written to another country's shape, which leaves the guard silent rather than
 * wrong.
 */
function postcode(tokens) {
  const houseNumberAt = tokens.findIndex(isNumber);
  for (let index = tokens.length - 1; index >= 0; index -= 1) {
    if (index === houseNumberAt && houseNumberAt < HOUSE_NUMBER_SEARCH_DEPTH) return null;
    if (tokens[index].length === 5 && isNumber(tokens[index])) return tokens[index];
  }
  return null;
}

/**
 * The address with everything the two hands writing it are free to differ on taken out of the way:
 * the part of the lot it names, and every word that has a short form.
 */
function loosened(tokens) {
  const kept = [];
  for (let index = 0; index < tokens.length; index += 1) {
    if (UNIT_DESIGNATORS.has(tokens[index])) {
      // "APT 3", "HSE B" — the designator and what it numbers go together. A longer word after it is
      // the rest of the address ("HSE LOS ANGELES") and stays.
      if (index + 1 < tokens.length && isUnitNumber(tokens[index + 1])) index += 1;
      continue;
    }
    kept.push(ABBREVIATIONS[tokens[index]] || tokens[index]);
  }
  return kept;
}

/**
 * The same address, or one written so much shorter than the other that it is contained in it — a
 * bill that leaves out the city, a setup that leaves out the postcode.
 */
function sameText(one, other) {
  if (!one || !other) return false;
  if (one === other) return true;
  return Math.min(one.length, other.length) >= MIN_CONTAINMENT_LENGTH && (one.includes(other) || other.includes(one));
}
const contradict = (one, other) => Boolean(one) && Boolean(other) && one !== other;

/**
 * How well the address a provider printed on a bill answers to an address a property is known by: 2
 * for the same words, 1 for the same place written another way, 0 for neither. The two are written
 * by different hands — a meter reader's system and a property manager typing a portfolio in — so
 * they agree on the place far more often than on the words: "1074 S Genesee Ave, Los Angeles, CA
 * 90019" and "1074 SGENESEE AVE HSE LOS ANGELES, CA 90019" are one address written twice.
 */
export function compareAddresses(left, right) {
  const one = tokenize(left);
  const other = tokenize(right);
  if (!one.length || !other.length) return 0;

  // Whatever the rest reads like, a different house number, unit or postcode is a different address,
  // and allocating a bill to it charges the wrong residents. Only what both sides say counts.
  if (contradict(houseNumber(one), houseNumber(other)) || contradict(unitNumber(one), unitNumber(other)) || contradict(postcode(one), postcode(other))) {
    return 0;
  }
  if (sameText(one.join(''), other.join(''))) return 2;
  return sameText(loosened(one).join(''), loosened(other).join('')) ? 1 : 0;
}

/**
 * Narrows the company's properties down to the one the bill is for. An address written exactly as a
 * property is known leaves the properties it only resembles out, so a portfolio holding both a lot
 * and a house on it still resolves the one the bill names. More than one property left at the same
 * closeness is not a match, since guessing would allocate a bill against the wrong residents.
 */
export function findProperty(address, properties) {
  const scored = properties.map(property => ({
    property,
    match: compareAddresses(address, property.propertyAddress)
  })).filter(candidate => candidate.match > 0);
  if (!scored.length) return null;
  const closest = Math.max(...scored.map(candidate => candidate.match));
  const best = scored.filter(candidate => candidate.match === closest);
  return best.length === 1 ? best[0].property : null;
}

/**
 * The utility a word stands for, keyed to the types the system allocates against: Water, Sewer, Gas,
 * Electricity, Trash, Landscaping, Internet and Cable/Satellite. Types that are separate in the
 * setup but the same utility (Electric and Lighting, Irrigation and Landscaping, Sanitation and
 * Trash) collapse the way the property service already maps them.
 *
 * Both sides of a match are free text: bills say "ELECTRIC SERVICE" or "Refuse Collection", and a
 * property utility is named after its types, as in "Sewer/Water" or "Gas for Hot Water". Longer
 * words come first, so "wastewater" is read as sewer rather than as waste. A word that carries a
 * utility word without naming it stands for nothing, so that stormwater is not billed as water.
 */
const UTILITY_KEYWORDS = [['storm', null], ['wastewater', 'Sewer'], ['sewage', 'Sewer'], ['sewer', 'Sewer'], ['water', 'Water'], ['electric', 'Electricity'], ['lighting', 'Electricity'], ['gas', 'Gas'], ['propane', 'Gas'], ['trash', 'Trash'], ['garbage', 'Trash'], ['refuse', 'Trash'], ['sanitation', 'Trash'], ['waste', 'Trash'], ['landscaping', 'Landscaping'], ['irrigation', 'Landscaping'], ['internet', 'Internet'], ['broadband', 'Internet'], ['cable', 'Cable/Satellite'], ['satellite', 'Cable/Satellite'], ['television', 'Cable/Satellite']];

/**
 * The utilities a name covers. A name can cover several ("Sewer/Water"), and can mention a second
 * utility only to qualify the first ("Gas for Hot Water" is gas), so each part is named after the
 * first utility word in it.
 */
export function getUtilityKinds(name) {
  // The separators between utilities have to survive normalization, unlike the rest of the noise.
  const text = String(name || '').toLowerCase().replace(/[^a-z0-9/+,&]+/g, ' ').trim();
  if (!text) return [];
  const kinds = text.split(/\s(?:and|plus)\s|[/+,&]/).map(part => {
    const found = UTILITY_KEYWORDS.map(_ref => {
      let _ref2 = _slicedToArray(_ref, 2),
        keyword = _ref2[0],
        kind = _ref2[1];
      return [part.indexOf(keyword), kind];
    }).filter(_ref3 => {
      let _ref4 = _slicedToArray(_ref3, 1),
        index = _ref4[0];
      return index >= 0;
    });
    if (!found.length) return null;
    return found.reduce((best, current) => current[0] < best[0] ? current : best)[1];
  }).filter(Boolean);
  return [...new Set(kinds)];
}

/** One utility per id: a utility shared by several buildings is still a single choice. */
function uniqueUtilities(property) {
  const seen = new Set();
  return ((property === null || property === void 0 ? void 0 : property.utilities) || []).filter(utility => {
    if (!(utility !== null && utility !== void 0 && utility.utilityId) || seen.has(utility.utilityId)) return false;
    seen.add(utility.utilityId);
    return true;
  });
}
const normalizeProvider = value => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

/**
 * Whether two provider names stand for the same provider. Bills are printed with the short name a
 * provider is known by and setups are typed with the long one, so an abbreviation the other name
 * carries ("LADWP" in "Los Angeles Department of Water and Power (LADWP)") counts. Two letters are
 * too little to go on and are only accepted as the whole name.
 */
function isSameProvider(left, right) {
  const one = normalizeProvider(left);
  const other = normalizeProvider(right);
  if (!one || !other) return false;
  if (one === other) return true;
  return Math.min(one.length, other.length) >= 3 && (one.includes(other) || other.includes(one));
}

/**
 * Every utility on the property that covers the billed service. A bill for water is covered by a
 * "Sewer/Water" utility, so an overlap is enough; when several overlap, the provider that sent the
 * bill and then a utility covering exactly the billed service narrow them down. A property can buy
 * one utility from two providers — electricity from the city and from a second supplier — and then
 * only the provider tells them apart. A provider none of them is bought from narrows nothing, since
 * the name printed on a bill and the name typed into the setup need not agree.
 */
export function findUtilities(serviceType, property, providerName) {
  const wanted = getUtilityKinds(serviceType);
  if (!wanted.length) return {
    best: null,
    matches: []
  };
  const covering = uniqueUtilities(property).filter(utility => getUtilityKinds(utility.utilityType).some(kind => wanted.includes(kind)));
  const fromProvider = covering.filter(utility => isSameProvider(providerName, utility.utilityProviderName));
  const matches = fromProvider.length ? fromProvider : covering;

  // A kind can carry a slash of its own ("Cable/Satellite"), so the sets are keyed on another sign.
  const wantedKey = [...wanted].sort().join('+');
  const exact = matches.filter(utility => getUtilityKinds(utility.utilityType).sort().join('+') === wantedKey);
  if (exact.length === 1) return {
    best: exact[0],
    matches
  };
  return {
    best: matches.length === 1 ? matches[0] : null,
    matches
  };
}

/** A utility is named after its types, but only the provider tells two of the same type apart. */
function toUtilityOption(utility) {
  const type = String(utility.utilityType || '').trim();
  const provider = String(utility.utilityProviderName || '').trim();
  const label = [type, provider && type ? "(".concat(provider, ")") : provider].filter(Boolean).join(' ');
  return {
    id: utility.utilityId,
    label: label || 'Unnamed utility'
  };
}
const findById = (propertyId, properties) => propertyId ? properties.find(property => property.propertyId === propertyId) || null : null;

/**
 * The property a bill line belongs to, from the most dependable answer to the least: a manual choice,
 * then the property the services named when they validated the bill, then the properties the bill was
 * uploaded for, then the address printed on it. A single uploaded property is the answer outright;
 * several of them, as on a bill one provider charges across properties, narrow the addresses worth
 * matching to those the uploader named.
 */
export function findBillProperty(_ref5) {
  let address = _ref5.address,
    assignedPropertyIds = _ref5.assignedPropertyIds,
    extractedPropertyId = _ref5.extractedPropertyId,
    override = _ref5.override,
    properties = _ref5.properties;
  const chosen = findById(override === null || override === void 0 ? void 0 : override.propertyId, properties);
  if (chosen) return chosen;
  const resolved = findById(extractedPropertyId, properties);
  if (resolved) return resolved;
  const assigned = (assignedPropertyIds || []).map(propertyId => findById(propertyId, properties)).filter(Boolean);
  if (assigned.length === 1) return assigned[0];
  if (assigned.length > 1) return findProperty(address, assigned);
  return findProperty(address, properties);
}

/**
 * Which utility of an already known property a bill line should be allocated against. Finding the
 * property is the expensive half and is the same for every line of one block, so it is asked for
 * separately.
 */
export function resolveUtilityTarget(_ref6) {
  let extractedUtilityId = _ref6.extractedUtilityId,
    override = _ref6.override,
    property = _ref6.property,
    providerName = _ref6.providerName,
    serviceType = _ref6.serviceType;
  if (!property) {
    return {
      hasUtilityMatch: false,
      needsProperty: true,
      needsUtility: false,
      propertyId: '',
      utilityId: '',
      utilityOptions: []
    };
  }
  const utilities = uniqueUtilities(property);
  const _findUtilities = findUtilities(serviceType, property, providerName),
    best = _findUtilities.best,
    matches = _findUtilities.matches;
  const byId = utilityId => utilityId ? utilities.find(utility => utility.utilityId === utilityId) : null;

  // A manual choice first, then the utility the services named when they validated the bill, but only
  // if it belongs to this property: a bill line moved to another property has to be matched afresh.
  const resolved = byId(override === null || override === void 0 ? void 0 : override.utilityId) || byId(extractedUtilityId) || best;
  if (resolved) {
    return {
      hasUtilityMatch: true,
      needsProperty: false,
      needsUtility: false,
      propertyId: property.propertyId,
      utilityId: resolved.utilityId,
      utilityOptions: utilities.map(toUtilityOption)
    };
  }

  // Either several utilities cover this service, or none does and any of them could be meant.
  return {
    hasUtilityMatch: matches.length > 0,
    needsProperty: false,
    needsUtility: true,
    propertyId: property.propertyId,
    utilityId: '',
    utilityOptions: (matches.length ? matches : utilities).map(toUtilityOption)
  };
}

/**
 * Works out which property and utility a bill line should be allocated against. When neither the
 * choice, the upload nor the address decides, the caller is told what is missing and which options to
 * offer.
 */
export function resolveAllocationTarget(_ref7) {
  let address = _ref7.address,
    assignedPropertyIds = _ref7.assignedPropertyIds,
    extractedPropertyId = _ref7.extractedPropertyId,
    extractedUtilityId = _ref7.extractedUtilityId,
    override = _ref7.override,
    properties = _ref7.properties,
    providerName = _ref7.providerName,
    serviceType = _ref7.serviceType;
  const property = findBillProperty({
    address,
    assignedPropertyIds,
    extractedPropertyId,
    override,
    properties
  });
  return resolveUtilityTarget({
    extractedUtilityId,
    override,
    property,
    providerName,
    serviceType
  });
}